import React, { useState, useEffect, useMemo } from 'react';
import { CustomModal } from '@/components/CustomModal';
import { Input, Tabs, Typography, Button, Spin, Alert, Tag, List } from 'antd';
import {
	SearchOutlined,
	GlobalOutlined,
	PhoneOutlined,
	InfoCircleOutlined,
	DownOutlined,
	UpOutlined,
} from '@ant-design/icons';
import { getSMSPricingInfo } from '@/api/smsPricing';
import { useQuery } from '@tanstack/react-query';
import { parsePhoneNumber, isValidPhoneNumber } from 'libphonenumber-js';
import { MODAL_IDS } from 'src/enums/EModal';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getModalData } from '@/store/modal/selector';
import { getSelectedAgent } from '@/store/account/selector';
import { getResellerData } from '@/store/reseller/selector';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

interface Country {
	code: string;
	zone: string;
	zoneName: string;
	zoneDescription: string;
	basePrice: number;
	formattedPrice: string;
	disabled: boolean;
}

// New interface for the simplified country structure
interface EnabledCountry {
	zone: string;
	name: string;
	code: string;
	basePrice: number;
}

interface Zone {
	id: string;
	name: string;
	description: string;
	basePrice: number;
	formattedPrice: string;
	countryCount: number;
}

interface PricingMetadata {
	totalCountries: number;
	totalZones: number;
	lowestPrice: number;
	highestPrice: number;
	updatedAt: string;
}

interface PricingData {
	countries: Country[];
	zones: Zone[];
	enabledCountries: EnabledCountry[]; // Add the new field
	metadata: PricingMetadata;
}

const SMSPricingModal = () => {
	const selectedAgent = useAppSelector(getSelectedAgent);
	const currentReseller = useAppSelector(getResellerData);
	const modalData = useAppSelector(getModalData) as {
		planId?: string;
	};
	const [activeTab, setActiveTab] = useState<string>('countries');
	const [searchText, setSearchText] = useState<string>('');
	const [phoneNumber, setPhoneNumber] = useState<string>('');
	const [phoneCountry, setPhoneCountry] = useState<Country | null>(null);
	const [phoneError, setPhoneError] = useState<string>('');
	const [selectedCountry, setSelectedCountry] = useState<EnabledCountry | null>(null);

	// Fetch SMS pricing data
	const { data, isLoading, error } = useQuery({
		queryKey: ['getSMSPricingInfo', modalData?.planId],
		queryFn: async ({ queryKey }) => {
			const [_, planId] = queryKey;
			return getSMSPricingInfo({
				planId: planId as string | undefined,
				clientType:
					currentReseller?.emailAddress && !selectedAgent?.reseller_email
						? 'RESELLER'
						: 'RECEPTIONIST',
			});
		},
	});

	const pricingData: PricingData | undefined = data?.data as PricingData | undefined;

	// Reset phone lookup when modal closes
	useEffect(() => {
		setPhoneNumber('');
		setPhoneCountry(null);
		setPhoneError('');
		setSelectedCountry(null);
		setSearchText('');
	}, []);

	// Filter countries based on search text
	const filteredCountries = useMemo(() => {
		if (!pricingData?.enabledCountries?.length) {
			return [];
		}

		if (!searchText) {
			return pricingData.enabledCountries;
		}

		const lowerCaseSearch = searchText.toLowerCase().trim();
		const upperCaseSearch = searchText.toUpperCase().trim();

		// Check for exact country code match first
		const exactCodeMatches = pricingData.enabledCountries.filter(
			(country) => country.code === upperCaseSearch,
		);

		// If we have exact matches by code, return only those
		if (exactCodeMatches.length > 0) {
			return exactCodeMatches;
		}

		// Handle common country abbreviations and alternative names
		const searchTerms = [lowerCaseSearch];

		// Map of common abbreviations and alternative names to their full country names
		const countryAliases: Record<string, string[]> = {
			us: ['united states', 'usa', 'america'],
			usa: ['united states', 'america'],
			uk: ['united kingdom', 'britain', 'great britain', 'england'],
			uae: ['united arab emirates'],
			aus: ['australia'],
			can: ['canada'],
			nz: ['new zealand'],
			russia: ['russian federation'],
			china: ['peoples republic of china', 'prc'],
			korea: ['south korea', 'republic of korea'],
			hk: ['hong kong'],
			sg: ['singapore'],
			jp: ['japan'],
			pak: ['pakistan'],
			ind: ['india'],
			mex: ['mexico'],
			bra: ['brazil'],
			ger: ['germany', 'deutschland'],
			fr: ['france'],
			it: ['italy', 'italia'],
			sp: ['spain', 'españa', 'espana'],
		};

		// Add aliases for the search term if they exist
		Object.entries(countryAliases).forEach(([alias, fullNames]) => {
			if (lowerCaseSearch === alias || fullNames.includes(lowerCaseSearch)) {
				searchTerms.push(...fullNames);
				searchTerms.push(alias);
			}
		});

		return pricingData.enabledCountries.filter((country) =>
			searchTerms.some(
				(term) =>
					country.code.toLowerCase().includes(term) ||
					country.zone.toLowerCase().includes(term) ||
					country.name.toLowerCase().includes(term),
			),
		);
	}, [pricingData?.enabledCountries, searchText]);

	// Handle phone number input change
	const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		// Clear previous results when input changes
		setPhoneNumber(e.target.value);
		setPhoneCountry(null);
		setPhoneError('');
	};

	// Handle phone number lookup
	const handlePhoneLookup = () => {
		// Clear previous results before starting a new search
		setPhoneCountry(null);

		if (!phoneNumber || !pricingData?.countries) {
			setPhoneError('Please enter a valid phone number');
			return;
		}

		try {
			// Validate phone number
			if (!isValidPhoneNumber(phoneNumber)) {
				setPhoneError('Invalid phone number format');
				return;
			}

			const parsedNumber = parsePhoneNumber(phoneNumber);
			const countryCode = parsedNumber.country;

			if (!countryCode) {
				setPhoneError('Could not determine country for this number');
				return;
			}

			// Find country in our pricing data
			const country = pricingData.countries.find((c) => c.code === countryCode);

			if (!country) {
				setPhoneError(`No pricing information available for ${countryCode}`);
				return;
			}

			// Check if the country is disabled
			if (country.disabled) {
				setPhoneError(
					`We currently don't support SMS to ${country.zoneDescription.split(',')[0]}. Please contact support for more information.`,
				);
				return;
			}

			// Get the country name
			const countryName = country.zoneDescription.split(',')[0].trim();

			// Set the country with a modified description that only includes the country name
			setPhoneCountry({
				...country,
				zoneDescription: countryName,
			});
			setPhoneError('');
		} catch {
			setPhoneError('Error processing phone number. Please check the format.');
		}
	};

	// Calculate credit cost
	const calculateCredits = (price: number): number => {
		// 1 SMS credit = 12¢
		return price / 0.12;
	};

	// Handle country selection
	const handleCountrySelect = (country: EnabledCountry) => {
		if (selectedCountry?.code === country.code) {
			setSelectedCountry(null); // Toggle off if already selected
		} else {
			setSelectedCountry(country); // Select new country
		}
	};

	// Render pricing information for a country
	const renderPricingInfo = (country: EnabledCountry | Country) => {
		const price =
			'formattedPrice' in country ? country.formattedPrice : `$${country.basePrice.toFixed(4)}`;

		return (
			<div className="bg-gray-50 rounded-lg p-4 mt-2">
				<div className="bg-white rounded-lg p-4 shadow-md">
					<div className="grid grid-cols-1 gap-4">
						<div className="flex flex-col gap-1">
							<Title level={5} className="m-0">
								Pricing Details
							</Title>
							<div className="flex items-center">
								<Tag color="blue">{price}</Tag>
							</div>
						</div>
						<div className="flex flex-col gap-1">
							<Text strong>Credits needed:</Text>
							<div className="flex items-center">
								<Tag color="blue">{calculateCredits(country.basePrice).toFixed(2)} credits</Tag>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	};

	return (
		<CustomModal
			title={
				<div className="flex items-center">
					<GlobalOutlined className="mr-2" />
					<span>SMS Pricing by Country</span>
				</div>
			}
			modalId={MODAL_IDS.SMS_PRICING}
			footer={null}
			width={800}
		>
			<div className="px-2.5">
				{isLoading ? (
					<div className="flex flex-col items-center justify-center py-10">
						<Spin size="large" />
						<Text className="mt-3">Loading pricing information...</Text>
					</div>
				) : error ? (
					<Alert
						message="Error Loading Pricing"
						description="There was an error loading the SMS pricing information. Please try again later."
						type="error"
						showIcon
					/>
				) : (
					<>
						<div className="mb-5">
							<Alert
								message="SMS Credits Explained"
								description={
									<div>
										<p>You can use SMS Credits to send text messages to your customers.</p>
										<p>
											<strong>1 SMS Credit = $0.12</strong>
										</p>
										<p>
											Rates may vary by country. For example, in the US and Canada, you can send 6
											text messages for every 1 SMS Credit.
										</p>
										<p>
											<strong>Great value!</strong> Your SMS credits are designed for maximum
											efficiency, making them incredibly cost-effective for staying in touch with
											your customers.
										</p>
									</div>
								}
								type="info"
								showIcon
								icon={<InfoCircleOutlined />}
							/>
						</div>

						<Tabs activeKey={activeTab} onChange={setActiveTab} className="mt-5">
							<TabPane
								tab={
									<span>
										<GlobalOutlined /> Countries
									</span>
								}
								key="countries"
							>
								<div>
									<div className="mb-4">
										<Input
											placeholder="Search for a country (e.g. USA, UK, Canada)"
											prefix={<SearchOutlined />}
											value={searchText}
											onChange={(e) => setSearchText(e.target.value)}
											className="max-w-md"
											allowClear
										/>
										{searchText && (
											<div className="mt-2">
												<Text type="secondary" className="text-xs">
													Found {filteredCountries.length} results for "{searchText}"
												</Text>
												{filteredCountries.length > 0 && (
													<Button
														type="link"
														size="small"
														onClick={() => setSearchText('')}
														className="ml-2"
													>
														Clear search
													</Button>
												)}
											</div>
										)}
									</div>

									<Paragraph className="mb-4">
										Click on a country to view detailed SMS pricing information.
									</Paragraph>

									{filteredCountries.length === 0 ? (
										<Alert
											message="No countries found"
											description="Try searching with a different term or abbreviation, or use the Phone Number Lookup tab to check specific numbers."
											type="info"
											showIcon
											className="mb-4"
										/>
									) : (
										<List
											dataSource={filteredCountries}
											renderItem={(country) => {
												const isSelected = selectedCountry?.code === country.code;

												return (
													<List.Item
														className="px-0 py-2 border-b border-gray-100"
														style={{ padding: 0, margin: 0 }}
													>
														<div className="w-full">
															<div
																className={`flex justify-between items-center p-3 cursor-pointer hover:bg-gray-50 rounded-md ${isSelected ? 'bg-blue-50' : ''}`}
																onClick={() => handleCountrySelect(country)}
															>
																<Text strong>{country.name}</Text>
																<div className="flex items-center">
																	{isSelected ? <UpOutlined /> : <DownOutlined />}
																</div>
															</div>

															{isSelected && renderPricingInfo(country)}
														</div>
													</List.Item>
												);
											}}
										/>
									)}
								</div>
							</TabPane>

							<TabPane
								tab={
									<span>
										<PhoneOutlined /> Phone Number Lookup
									</span>
								}
								key="lookup"
							>
								<div>
									<Paragraph className="mb-4">
										Enter a phone number to check which zone it belongs to and how much it will cost
										to send SMS messages to this number. This is the most accurate way to check
										pricing for a specific destination.
									</Paragraph>

									<div className="flex gap-2.5 max-w-lg">
										<Input
											placeholder="Enter phone number (e.g. +1234567890)"
											value={phoneNumber}
											onChange={handlePhoneNumberChange}
											className="flex-1"
											addonBefore={<PhoneOutlined />}
										/>
										<Button type="primary" onClick={handlePhoneLookup}>
											Check Pricing
										</Button>
									</div>

									{phoneError && (
										<Alert
											message="SMS Not Available"
											description={phoneError}
											type="error"
											showIcon
											className="mt-3"
										/>
									)}

									{phoneCountry && renderPricingInfo(phoneCountry)}
								</div>
							</TabPane>
						</Tabs>
					</>
				)}
			</div>
		</CustomModal>
	);
};

export default SMSPricingModal;
